import _ from "lodash";

/**
 * ICH's Portfolio prints from one phase of the home's contract, as its Invoice does (the player's Print Options):
 * the Agreement of Sale or one of its addenda, the one being worked on first. Full contract prints every selection
 * as of that phase; Changes only prints the selections an addendum added or changed from the document it amends.
 */
export const PORTFOLIO_SCOPE_FULL = "full";
export const PORTFOLIO_SCOPE_CHANGES = "changes";

const array = value => (Array.isArray(value) ? value : []);
const text = value => (value === undefined || value === null ? "" : String(value).trim());
const number = (value, fallback) => (Number.isFinite(Number(value)) && value !== "" && value !== null ? Number(value) : fallback);

// As the player's contractDocumentTitle.
const documentTitle = document => document.title || (document.kind === "initialPurchase" ? "Agreement of Sale" : `Addendum ${document.number || ""}`.trim());

const getContractDocuments = job => array(_.get(job, ["pricingContractProto", "documents"]))
	.filter(document => _.isPlainObject(document) && text(document.id));

/**
 * @param {Object} job A saved job.
 * @returns {{ id: string, title: string, status: string, isAddendum: boolean, active: boolean, baseId: string }[]}
 *   The contract's documents in order, when there is more than one to print from.
 */
export function getPortfolioPhases(job = {}) {
	const documents = getContractDocuments(job);
	if (documents.length < 2) {
		return [];
	}
	const activeId = text(_.get(job, ["pricingContractProto", "activeDocumentId"]));
	return documents.map((document, index) => ({
		id: text(document.id),
		title: documentTitle(document),
		status: document.status === "draft" ? "Draft" : "Locked",
		isAddendum: document.kind === "addendum",
		active: Boolean(activeId) && activeId === text(document.id),
		baseId: text(document.baseDocumentId) || (index > 0 ? text(documents[index - 1].id) : "")
	}));
}

export function getDefaultPortfolioPhaseId(phases = []) {
	const active = phases.find(phase => phase.active);
	return (active || phases[phases.length - 1] || {}).id || "";
}

const lineKey = line => text(line.lineId) || text(line.id) || `${text(line.productId)}|${text(line.description)}`;

// What a buyer signs for, as the player's Addendum Changes report: a new photo on the same line is not a change.
const lineSignature = line => JSON.stringify({
	id: text(line.id),
	productId: text(line.productId),
	lineType: text(line.lineType),
	settlesContractLineId: text(line.settlesContractLineId),
	description: text(line.description),
	note: text(line.note),
	qty: number(line.qty, 1),
	rate: number(line.rate, 0),
	discount: number(line.discount, 1),
	value: number(line.value, number(line.qty, 1) * number(line.rate, 0) * number(line.discount, 1)),
	allowanceCategory: text(_.get(line, ["allowance", "category"]))
});

/**
 * The lines a Portfolio prints for a phase: its saved lines (for the draft being worked on, the job's own or the
 * Price List's unsaved ones), or for Changes only those an addendum added or changed.
 *
 * @param {Object} job A saved job.
 * @param {string} [options.phaseId] The phase ("" for the one being worked on).
 * @param {string} [options.scope] PORTFOLIO_SCOPE_FULL or PORTFOLIO_SCOPE_CHANGES.
 * @param {Object[]} [options.draftLines] The Price List's unsaved lines.
 */
export function getPortfolioPhaseLines(job = {}, { phaseId = "", scope = PORTFOLIO_SCOPE_FULL, draftLines = null } = {}) {
	const liveLines = Array.isArray(draftLines) ? draftLines : array(job && job.invoiceItems);
	const phases = getPortfolioPhases(job);
	const phase = phases.find(candidate => candidate.id === phaseId)
		|| phases.find(candidate => candidate.id === getDefaultPortfolioPhaseId(phases));
	if (!phase) {
		return liveLines;
	}
	const documents = getContractDocuments(job);
	const document = documents.find(candidate => text(candidate.id) === phase.id);
	const lines = phase.active && document.status === "draft" ? liveLines : array(document.invoiceItems);
	if (scope !== PORTFOLIO_SCOPE_CHANGES || !phase.isAddendum) {
		return lines;
	}
	const base = documents.find(candidate => text(candidate.id) === phase.baseId);
	const baseSignatures = new Map(array(base && base.invoiceItems)
		.filter(line => _.isPlainObject(line))
		.map(line => [lineKey(line), lineSignature(line)]));
	return lines.filter(line => _.isPlainObject(line) && baseSignatures.get(lineKey(line)) !== lineSignature(line));
}
